import type { CSSProperties } from "react";

import { styles } from "@repo/helpers";
import { useMemo } from "react";

import type { MarkupProps } from "./types";

import { InputComponent } from "../input";
import style from "./style.module.scss";

export default function ToggleSliderMarkup(props: MarkupProps) {
	const {
		activeColor,
		inactiveColor,
		sliderBorderColor,
		hideLabel,
		rounded,
		small,
		size,
		trueLabel,
		falseLabel,
		onChange,
		...inputProps
	} = props;
	const { id, name, value, disabled } = inputProps;
	const labelPosition = hideLabel ? "hidden" : props.labelPosition;

	const formatValueIfNeeded = (v: string | number | boolean) => {
		if (typeof v != "string") return v;
		if (v.toLowerCase() === "true") return true;
		if (v.toLowerCase() === "false") return false;
		return v;
	};
	const inputPropsValue = useMemo(() => formatValueIfNeeded(value), [value, formatValueIfNeeded]);
	const propsValue = useMemo(() => formatValueIfNeeded(props.value), [props.value, formatValueIfNeeded]);

	const inlineStyle = useMemo(() => {
		const style = {} as CSSProperties;
		if (activeColor && inputPropsValue) {
			style.backgroundColor = activeColor;
		} else if (inactiveColor && !propsValue) {
			style.backgroundColor = inactiveColor;
		}
		if (sliderBorderColor) {
			style.borderColor = sliderBorderColor;
		}
		return style;
	}, [activeColor, inactiveColor, propsValue, sliderBorderColor, inputPropsValue]);

	const sliderStyle = styles(
		props.className,
		style.slider,
		(small || size === "sm") && style.smallLine,
		size === "sm" && style.mini,
		rounded && style.sliderRound,
		disabled && style.disabled,
	);

	const switchStyle = styles(
		style.switchLabel,
		(small || size === "sm") && style.smallLabel,
		labelPosition === "left" ? style.labelLeft : style.labelRight,
	);

	const switchGroup = (
		<label className={style.switchGroup}>
			<input
				type="checkbox"
				className={style.sliderInput}
				checked={!!inputPropsValue || false}
				disabled={disabled}
				onChange={onChange}
				id={id || name}
				name={name || id}
			/>
			<span className={sliderStyle} style={inlineStyle} />
		</label>
	);
	const switchLabel = <label className={switchStyle}>{inputPropsValue ? trueLabel : falseLabel}</label>;

	return (
		<InputComponent {...inputProps}>
			<div className={style.toggleGroup}>
				{labelPosition === "left" && switchLabel}
				{switchGroup}
				{labelPosition === "right" && switchLabel}
			</div>
		</InputComponent>
	);
}
